import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  ArrowPathIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import PageLayout from '../../components/PageLayout';
import { AccountSummary } from '../../components/account/AccountDetailContent';
import AccountDetailDrawer from '../../components/account/AccountDetailDrawer';
import InvoiceActionMenu from '../../components/invoices/InvoiceActionMenu';
import EntityDrawer from '../../components/ui/EntityDrawer';
import { adminAPI } from '../../services/api';

type AccountRecord = AccountSummary & {
  customer_count: number;
  created_at: string;
  updated_at: string;
};

export default function AdminAccounts() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [accountSearch, setAccountSearch] = useState('');
  const [accountPage, setAccountPage] = useState(1);
  const [newAccountName, setNewAccountName] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountRecord | null>(null);
  const accountsPageSize = 50;

  const { data: accountsData, isLoading: accountsLoading, error: accountsError } = useQuery(
    ['admin-accounts', accountSearch, accountPage],
    async () => {
      const response = await adminAPI.getAccounts({
        search: accountSearch || undefined,
        page: accountPage,
        limit: accountsPageSize,
      });
      return response.data;
    },
    {
      keepPreviousData: true,
    },
  );

  const accounts = useMemo<AccountRecord[]>(() => {
    return Array.isArray(accountsData?.accounts) ? accountsData.accounts : [];
  }, [accountsData?.accounts]);
  const accountsPagination = accountsData?.pagination || {
    page: accountPage,
    limit: accountsPageSize,
    total: accounts.length,
    totalPages: 1,
  };

  const createAccountMutation = useMutation(
    async (name: string) => {
      return adminAPI.createAccount({ name });
    },
    {
      onSuccess: () => {
        toast.success('Account saved');
        setNewAccountName('');
        setCreateDrawerOpen(false);
        queryClient.invalidateQueries('admin-accounts');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to save account');
      },
    },
  );

  const renameAccountMutation = useMutation(
    async (payload: { id: string; name: string }) => {
      return adminAPI.updateAccount(payload.id, { name: payload.name });
    },
    {
      onSuccess: (_response, payload) => {
        toast.success('Account renamed');
        queryClient.invalidateQueries('admin-accounts');
        queryClient.invalidateQueries('admin-account-customers');
        queryClient.invalidateQueries(['account-drawer-account', payload.id]);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to rename account');
      },
    },
  );

  const deleteAccountMutation = useMutation(
    async (accountId: string) => adminAPI.deleteAccount(accountId),
    {
      onSuccess: () => {
        toast.success('Account removed');
        queryClient.invalidateQueries('admin-accounts');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to remove account');
      },
    },
  );

  const backfillMutation = useMutation(
    async () => {
      return adminAPI.backfillAccounts();
    },
    {
      onSuccess: (response: any) => {
        const payload = response?.data || {};
        const inserted = Number(payload.inserted_accounts || 0);
        const linked = Number(payload.linked_customers || 0);
        const createdCustomers = Number(payload.created_customers || 0);
        const linkedInvoices = Number(payload.linked_invoices || 0);
        toast.success(
          `Backfill complete. Added ${inserted} account${inserted === 1 ? '' : 's'}, linked ${linked} customer${linked === 1 ? '' : 's'}, created ${createdCustomers} customer${createdCustomers === 1 ? '' : 's'}, linked ${linkedInvoices} invoice${linkedInvoices === 1 ? '' : 's'}.`,
        );
        queryClient.invalidateQueries('admin-accounts');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to backfill account names');
      },
    },
  );

  const totalAttachedCustomers = useMemo(
    () => accounts.reduce((sum, account) => sum + Number(account.customer_count || 0), 0),
    [accounts],
  );

  useEffect(() => {
    const handleDocumentClick = () => setOpenMenuId(null);
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  const handleCreateAccount = () => {
    const trimmed = newAccountName.trim();
    if (!trimmed) {
      toast.error('Account name is required');
      return;
    }
    createAccountMutation.mutate(trimmed);
  };

  const openAccountDrawer = (account: AccountRecord) => {
    setSelectedAccount(account);
  };

  const handleRenameAccount = (account: AccountRecord) => {
    const nextName = window.prompt('Rename account', account.name);
    if (nextName == null) {
      return;
    }

    const trimmed = nextName.trim();
    if (!trimmed || trimmed === account.name) {
      return;
    }

    renameAccountMutation.mutate({ id: account.id, name: trimmed });
  };

  const handleDeleteAccount = (account: AccountRecord) => {
    const confirmed = window.confirm(`Delete ${account.name}? Attached customers will be detached.`);
    if (!confirmed) {
      return;
    }

    deleteAccountMutation.mutate(account.id);
  };

  return (
    <>
      <PageLayout className="space-y-6">
        <div className="space-y-6">
          <>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Accounts</h1>
            <p className="mt-1 text-on-surface-variant">
              Manage organizations, attached customers, billing contacts, and account-level quote terms.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => backfillMutation.mutate()}
              disabled={backfillMutation.isLoading}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <ArrowPathIcon className="h-4 w-4" />
              {backfillMutation.isLoading ? 'Backfilling...' : 'Backfill'}
            </button>
            <button
              type="button"
              onClick={() => setCreateDrawerOpen(true)}
              className="btn-primary inline-flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Create Account
            </button>
          </div>
        </div>

        {accountsError && (
          <div className="rounded-card border border-error/40 bg-error-container/20 px-4 py-3 text-sm text-on-surface">
            Failed to load accounts from the API. {(accountsError as any)?.response?.data?.message || (accountsError as any)?.message || 'Unknown error'}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="admin-subpanel rounded-card p-4">
            <p className="text-sm text-on-surface-variant">Total Accounts</p>
            <p className="mt-1 text-2xl font-semibold text-on-surface">{accountsPagination.total}</p>
          </div>
          <div className="admin-subpanel rounded-card p-4">
            <p className="text-sm text-on-surface-variant">Attached Customers on Page</p>
            <p className="mt-1 text-2xl font-semibold text-on-surface">{totalAttachedCustomers}</p>
          </div>
        </div>

        <section className="admin-table-card">
          <div className="admin-table-toolbar flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div>
              <h2 className="text-base font-semibold text-on-surface">Account Directory</h2>
              <p className="text-xs text-on-surface-variant">
                Row click opens account details.
              </p>
            </div>
            <div className="relative w-full max-w-sm">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
              <input
                type="text"
                value={accountSearch}
                onChange={(event) => {
                  setAccountSearch(event.target.value);
                  setAccountPage(1);
                }}
                className="input-field pl-9"
                placeholder="Search accounts..."
              />
            </div>
          </div>

          {accountsLoading ? (
            <p className="px-4 py-8 text-center text-sm text-on-surface-variant">Loading accounts...</p>
          ) : accounts.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <UserGroupIcon className="mx-auto h-10 w-10 text-on-surface-variant" />
              <p className="mt-3 text-sm text-on-surface-variant">No accounts found.</p>
              <button
                type="button"
                onClick={() => setCreateDrawerOpen(true)}
                className="btn-primary mt-4 inline-flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Create Account
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="admin-table-grid min-w-full text-sm">
                  <thead className="admin-table-head">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Account</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Customers</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Updated</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="admin-table-body">
                    {accounts.map((account) => (
                      <tr
                        key={account.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openAccountDrawer(account)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openAccountDrawer(account);
                          }
                        }}
                        className="admin-table-row cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <span className="admin-avatar-tile flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl">
                              <BuildingOfficeIcon className="h-4 w-4" />
                            </span>
                            <div className="min-w-0">
                              <p className="truncate font-medium text-on-surface">{account.name}</p>
                              <p className="truncate text-xs text-on-surface-variant">Account record</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-on-surface">
                          {account.customer_count} customer{account.customer_count === 1 ? '' : 's'}
                        </td>
                        <td className="px-4 py-3 text-on-surface-variant">
                          {account.updated_at ? new Date(account.updated_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-right" onClick={(event) => event.stopPropagation()}>
                          <InvoiceActionMenu
                            menuId={account.id}
                            openMenuId={openMenuId}
                            setOpenMenuId={setOpenMenuId}
                            invoiceNumber={account.name}
                            actions={[
                              {
                                label: 'Open details',
                                onClick: () => openAccountDrawer(account),
                              },
                              {
                                label: 'Open full page',
                                onClick: () => navigate(`/admin/accounts/${account.id}`),
                              },
                              {
                                label: 'Rename account',
                                onClick: () => handleRenameAccount(account),
                              },
                              {
                                label: 'Remove account',
                                dangerous: true,
                                onClick: () => handleDeleteAccount(account),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-outline/60 px-4 py-3 text-sm text-on-surface-variant">
                <span>
                  Page {accountsPagination.page} of {accountsPagination.totalPages} ({accountsPagination.total} total)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAccountPage((page) => Math.max(page - 1, 1))}
                    disabled={accountPage <= 1 || accountsLoading}
                    className="btn-secondary"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccountPage((page) => Math.min(page + 1, accountsPagination.totalPages))}
                    disabled={accountPage >= accountsPagination.totalPages || accountsLoading}
                    className="btn-secondary"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
          </>
        </div>
      </PageLayout>
      <EntityDrawer
      open={createDrawerOpen}
      onClose={() => setCreateDrawerOpen(false)}
      title="Create Account"
      subtitle="Create a durable organization record for customers, invoices, quotes, and reservations."
      widthClassName="max-w-xl"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={() => setCreateDrawerOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreateAccount}
            disabled={createAccountMutation.isLoading}
            className="btn-primary inline-flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            {createAccountMutation.isLoading ? 'Saving...' : 'Save Account'}
          </button>
        </>
      }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-on-surface">Account name</label>
            <input
              type="text"
              value={newAccountName}
              onChange={(event) => setNewAccountName(event.target.value)}
              placeholder="Pacific Expeditions"
              className="input-field"
              autoFocus
            />
            <p className="mt-2 text-sm text-on-surface-variant">
              Account names are normalized to avoid duplicate organizations with different spacing or casing.
            </p>
          </div>
        </div>
      </EntityDrawer>
      <AccountDetailDrawer
        open={Boolean(selectedAccount)}
        onClose={() => setSelectedAccount(null)}
        account={selectedAccount}
        onAccountUpdated={() => {
          queryClient.invalidateQueries('admin-accounts');
        }}
      />
    </>
  );
}
